import { useEffect, useRef, useState } from "react";
import { Copy, Send, ScanLine, CheckCircle2, ClipboardPaste, Wallet, PlayCircle, MessageCircle } from "lucide-react";
import { openChat } from "@/lib/api";

const ANY = [
  [Copy, "Copy our address", "Tap the copy button above (or scan the QR). Check the network matches the one you picked."],
  [Wallet, "Open USDT in your wallet", "In your wallet or exchange, open USDT and tap Send (exchanges call it Withdraw)."],
  [ClipboardPaste, "Paste, pick network, enter amount", "Paste our address, choose the SAME network, and enter the exact amount shown above."],
  [CheckCircle2, "Confirm and send", "Confirm the transfer and wait for the success screen."],
  [Copy, "Copy the transaction ID", "Open the transfer in your history and copy the Transaction ID (also called TxID or Hash)."],
  [Send, "Paste it below", "Paste it in step 3 and tap Submit before the timer ends."],
];

const TRUST = [
  [Wallet, "Open USDT", "Open Trust Wallet and tap USDT. Check the network tag matches (TRC20, BEP20, ERC20 or SOL)."],
  [Send, "Tap Send", "Tap the Send arrow at the top of the USDT screen."],
  [ScanLine, "Paste or scan our address", "Paste our address or tap the scan icon to scan the QR above. Enter the exact amount, tap Next, then Confirm."],
  [CheckCircle2, "Open the transfer", "When it's sent, tap the transfer in your USDT history."],
  [Copy, "Copy the hash", "Tap 'More details' to open the explorer and copy the Transaction Hash."],
  [ClipboardPaste, "Paste it below", "Paste it in step 3 and tap Submit before the timer ends."],
];

const CAPTIONS = ["Open USDT and tap Send", "Paste our address + exact amount", "Confirm the transfer", "Open it in your history", "Copy the Transaction ID", "Paste it here and Submit"];

const Steps = ({ steps, id }) => {
  const [on, setOn] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { setOn(0); setPaused(false); }, [steps]);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setOn((i) => (i + 1) % steps.length), 3000);
    return () => clearInterval(t);
  }, [steps, paused]);
  return (
    <ol data-testid={`guide-steps-${id}`} className="space-y-2">
      {steps.map(([Icon, title, text], i) => (
        <li key={title} onClick={() => { setOn(i); setPaused(true); }} data-testid={`guide-step-${id}-${i + 1}`}
          className={`flex gap-3 p-3 rounded-md border cursor-pointer transition-[background-color,border-color,transform] duration-500 rise ${on === i ? "border-emerald-500/60 bg-emerald-500/10 translate-x-1" : "border-white/5 bg-[#08090C]/50"}`}
          style={{ animationDelay: `${i * 70}ms` }}>
          <span className={`w-8 h-8 shrink-0 grid place-items-center rounded-full font-mono text-xs font-bold transition-colors duration-500 ${on === i ? "bg-emerald-500 text-[#08090C]" : "bg-white/5 text-gray-400"}`}>{i + 1}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold flex items-center gap-2"><Icon size={14} className={on === i ? "text-emerald-400" : "text-gray-500"} />{title}</p>
            <p className="text-xs text-gray-400 mt-0.5">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
};

const Clip = () => {
  const ref = useRef(null);
  const [cap, setCap] = useState(0);
  const onTime = () => { const v = ref.current; if (v?.duration) setCap(Math.min(CAPTIONS.length - 1, Math.floor((v.currentTime / v.duration) * CAPTIONS.length))); };
  return (
    <div className="relative mx-auto w-full max-w-[240px] aspect-[9/16] rounded-xl overflow-hidden border border-white/10 bg-black">
      <video data-testid="guide-video" ref={ref} src="/guide/pay-with-usdt.mp4" poster="/guide/poster.jpg" autoPlay muted loop playsInline preload="metadata" onTimeUpdate={onTime} className="w-full h-full object-cover" />
      <p data-testid="guide-video-caption" className="absolute inset-x-2 bottom-2 px-3 py-2 rounded-md bg-[#08090C]/85 backdrop-blur text-xs font-semibold text-center">
        <span className="text-emerald-400 font-mono mr-1">{cap + 1}.</span>{CAPTIONS[cap]}
      </p>
    </div>
  );
};

export const PaymentGuide = () => {
  const [tab, setTab] = useState("any");
  const [open, setOpen] = useState(false);
  return (
    <details data-testid="payment-guide" open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className="rounded-lg border border-white/10 bg-[#161922] group">
      <summary data-testid="payment-guide-toggle" className="cursor-pointer list-none flex items-center gap-2 px-4 h-12 text-sm font-semibold select-none">
        <PlayCircle size={18} className="text-emerald-400" /> How to send USDT and copy the hash
        <span className="ml-auto text-xs text-gray-500 group-open:hidden">Show guide</span>
      </summary>
      {open && (
        <div className="px-4 pb-4 grid md:grid-cols-[240px_1fr] gap-5 items-start">
          <Clip />
          <div className="space-y-3 min-w-0">
            <div className="inline-flex p-1 rounded-md bg-[#08090C] border border-white/10">
              {[["any", "Any wallet / exchange"], ["trust", "Trust Wallet"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`guide-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-3 h-10 sm:h-8 rounded text-xs font-semibold transition-colors ${tab === k ? "bg-emerald-500 text-[#08090C]" : "text-gray-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <Steps id={tab} steps={tab === "any" ? ANY : TRUST} />
            <button type="button" data-testid="guide-chat-help" onClick={() => openChat("Hi, I need help paying for my order: ")}
              className="w-full h-10 rounded-md border border-white/10 text-sm text-gray-300 inline-flex items-center justify-center gap-2 hover:border-emerald-500/60"><MessageCircle size={15} /> Stuck? Ask us on live chat</button>
          </div>
        </div>
      )}
    </details>
  );
};
